import React from 'react';
import { Box, Button, Flex, IconButton, Text, Tooltip } from '@chakra-ui/react';
import { faTimes, faMagic, faComments } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import c from '../../constant';
import { useAiPanel } from './AiPanelContext';

// Width of the docked panel. Exported so Layout can reserve exactly this much
// space for page content — the panel pushes the layout rather than covering
// it, so a user can read the record and the AI response side by side (this is
// the behaviour Chrome's side panel has, and the reason it is useful for
// reviewing a document).
const AI_PANEL_WIDTH = '380px';

// Container for the page's AI tools. There is no free-text chat here: the
// panel renders whatever the current page registered — its own action UI
// (`actions`) and optional prompt buttons (`suggestions`) — so each feature
// owns its own interaction rather than routing through a generic prompt box.
// `topOffset` is the live header height supplied by Layout, so the panel stays
// flush beneath a header whose height varies (maintenance banner, hidden
// header) rather than assuming a constant.
function AiPanel({ topOffset = 40 }) {
	const { t } = useTranslation();
	const { isOpen, closePanel, pageContext } = useAiPanel();

	if (!isOpen) return null;

	return (
		<Flex
			className="hidden-print"
			direction="column"
			position="fixed"
			top={`${topOffset}px`}
			right="0"
			bottom="0"
			w={{ base: '100%', md: AI_PANEL_WIDTH }}
			bg="white"
			borderLeft="1px solid #dbdbdb"
			boxShadow="-2px 0 6px rgba(0,0,0,0.06)"
			zIndex={1000}
		>
			<Flex align="center" justify="space-between" px="12px" py="10px" borderBottom="1px solid #dbdbdb" bg="#FAFAFA" flexShrink={0}>
				<Flex align="center" gap="8px" minW={0}>
					<FontAwesomeIcon icon={faMagic} style={{ fontSize: 14, color: '#2f855a' }} />
					<Text fontSize="14px" fontWeight="bold" noOfLines={1}>
						{t('AI Assistant')}
					</Text>
				</Flex>
				<Tooltip label={t('Close')}>
					<IconButton
						aria-label="close-ai-panel"
						size="xs"
						variant="ghost"
						icon={<FontAwesomeIcon icon={faTimes} />}
						onClick={closePanel}
					/>
				</Tooltip>
			</Flex>

			{pageContext?.title && (
				<Box px="12px" py="8px" borderBottom="1px solid #ebebeb" flexShrink={0}>
					<Text fontSize="11px" color="gray.500" textTransform="uppercase">
						{t('Context')}
					</Text>
					<Text fontSize="13px" fontWeight="600" noOfLines={2}>
						{pageContext.title}
					</Text>
					{pageContext?.subtitle && (
						<Text fontSize="12px" color="gray.600" noOfLines={1}>
							{pageContext.subtitle}
						</Text>
					)}
				</Box>
			)}

			<Box flex="1" overflowY="auto" px="12px" py="12px">
				{pageContext ? (
					<>
						{/* Page-supplied tools (e.g. the spelling review) render above the
						    prompt suggestions, since they are the concrete actions a
						    reviewer came here to run. */}
						{pageContext?.actions ? <Box mb="10px">{pageContext.actions}</Box> : null}
						{(pageContext?.suggestions || []).map((suggestion, index) => (
							<Button
								key={`ai-suggestion-${index}`}
								size="sm"
								variant="outline"
								w="100%"
								mb="6px"
								justifyContent="flex-start"
								fontWeight="400"
								fontSize="13px"
								whiteSpace="normal"
								h="auto"
								py="8px"
								textAlign="left"
								borderRadius={c.borderRadius}
								onClick={() => pageContext?.onSuggestion?.(suggestion)}
							>
								{t(suggestion)}
							</Button>
						))}
					</>
				) : (
					<Flex direction="column" align="center" justify="center" h="100%" pt="40px" color="gray.500">
						<FontAwesomeIcon icon={faComments} style={{ fontSize: 28, marginBottom: 10, opacity: 0.5 }} />
						<Text fontSize="13px" textAlign="center">
							{t('This page has no AI context yet.')}
						</Text>
					</Flex>
				)}
			</Box>
		</Flex>
	);
}

export { AI_PANEL_WIDTH };
export default AiPanel;
